import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';

import {
    DivisionRegistration,
    Grade,
    SeedingCriteria,
    SkillLevel,
    TimeZone,
    UsawMembership,
    WeightClass,
} from '@reg/shared/model';
import { DivisionGroupingService, RegistrationTypeService } from '@reg/shared/service';
import { format } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { DateFormat } from '../enum';
import { convertToDateTime } from '../helper/dateTime-helper';

/**
 * Model to hold a division data for wrestling. It's used for grouping/categorizing
 * wrestlers together for bracketing and event management purposes
 */
export class Division {
    public id: string = null;
    public name: string = null;
    public eventId: string = null;
    public eventTimeZone: string;
    public sequence: number = 1;
    public divisionGroupingType: string = DivisionGroupingService.BIRTHDATE;
    public weighInStartDateTime: Date = null;
    public weighInEndDateTime: Date = null;
    public youngestBirthdate: Date = null;
    public oldestBirthdate: Date = null;
    public youngestGrade: Grade = null;
    public oldestGrade: Grade = null;
    public measurementUnit: string = 'lbs';
    public weightClasses: WeightClass[] = [];
    public skillLevels: SkillLevel[] = [];
    public seedingCriteria: SeedingCriteria[] = [];
    public divisionRegistration: DivisionRegistration = null;
    public divisionProfileId: string = null;
    public isUsaWrestlingCardNumRequired: boolean = false;
    public publishWrestlersDateTime: Date = null;
    public isDual: boolean = null;
    public isVarsity: boolean = false;
    public isYoungestStrict: boolean = false;
    public usawMemberships: UsawMembership[];
    public isExpanded: boolean = false;
    public regName: string = null;
    public floLevelId: string = null;

    public static getFields(): string[] {
        return [
            'name',
            'eventId',
            'eventTimeZone',
            'sequence',
            'divisionGroupingType',
            'weighInStartDateTime',
            'weighInEndDateTime',
            'youngestBirthdate',
            'oldestBirthdate',
            'youngestGrade',
            'oldestGrade',
            'measurementUnit',
            'weightClasses',
            'divisionRegistration',
            'divisionProfileId',
            'isUsaWrestlingCardNumRequired',
            'publishWrestlersDateTime',
            'skillLevels',
            'seedingCriteria',
            'isDual',
            'isVarsity',
            'isYoungestStrict',
            'usawMemberships',
            'floLevelId',
        ];
    }

    /**
     * Constructor
     */
    constructor(eventId: string = '', isExpanded: boolean = false) {
        this.eventId = eventId;
        this.isExpanded = isExpanded;
    }

    public hasRegistration(): boolean {
        return this.divisionRegistration &&
            this.divisionRegistration.registrationType.key !== RegistrationTypeService.NONE;
    }

    public isOnlineRegistrationAllowed(): boolean {
        return this.hasRegistration() &&
            (this.divisionRegistration.registrationType.key === RegistrationTypeService.INVITE_ONLY ||
                this.divisionRegistration.registrationType.key === RegistrationTypeService.ONLINE_ONLY ||
                this.divisionRegistration.registrationType.key === RegistrationTypeService.ONSITE_ONLINE);
    }

    public isOnlinePaymentAllowed(): boolean {
        return this.hasRegistration() && this.divisionRegistration.hasOnlinePayment();
    }

    public isTeamRegistration(): boolean {
        return this.hasRegistration() &&
            this.divisionRegistration.registrationType.key === RegistrationTypeService.INVITE_ONLY;
    }

    public isOnsitePaymentAllowed(): boolean {
        return this.hasRegistration() && this.divisionRegistration.hasOnsitePayment();
    }

    public isOnlineRegistrationOpen(): boolean {
        if (this.isOnlineRegistrationAllowed()) {
            const nowLocal: Date = new Date();

            if (nowLocal > this.divisionRegistration.onlineStartDateTime &&
                nowLocal < this.divisionRegistration.onlineEndDateTime) {
                return true;
            }
        }

        return false;
    }

    public formatWeightClasses(): string {
        return this.weightClasses && this.weightClasses.map((x: WeightClass) => x.name).join(', ');
    }

    public getWeighInDateTimesInvalidMessage(): string {
        if (this.weighInStartDateTime && !this.weighInEndDateTime) {
            return 'Please enter a Weigh-in End date time.';
        } if (!this.weighInStartDateTime && this.weighInEndDateTime) {
            return 'Please enter a Weigh-in Start date time.';
        } if (this.weighInStartDateTime && this.weighInEndDateTime) {
            if (this.weighInEndDateTime < this.weighInStartDateTime) {
                return 'The weigh-in start cannot be after the end date time.';
            }
        }

        return null;
    }

    public clone(): Division {
        const clone: Division = new Division(this.eventId);
        clone.id = this.id;
        clone.name = this.name;
        clone.sequence = this.sequence;
        clone.divisionGroupingType = this.divisionGroupingType;
        clone.measurementUnit = this.measurementUnit;
        clone.divisionProfileId = this.divisionProfileId;
        clone.isUsaWrestlingCardNumRequired = this.isUsaWrestlingCardNumRequired;
        clone.isDual = this.isDual;
        clone.isVarsity = this.isVarsity;
        clone.isYoungestStrict = this.isYoungestStrict;
        clone.usawMemberships = this.usawMemberships && this.usawMemberships.map((m: UsawMembership) => m.clone());
        clone.eventTimeZone = this.eventTimeZone;
        clone.isExpanded = this.isExpanded;
        clone.regName = this.regName;
        clone.floLevelId = this.floLevelId;

        if (this.weighInStartDateTime) {
            clone.weighInStartDateTime = new Date(this.weighInStartDateTime.getTime());
        }
        if (this.weighInEndDateTime) {
            clone.weighInEndDateTime = new Date(this.weighInEndDateTime.getTime());
        }
        if (this.youngestBirthdate) {
            clone.youngestBirthdate = new Date(this.youngestBirthdate.getTime());
        }
        if (this.oldestBirthdate) {
            clone.oldestBirthdate = new Date(this.oldestBirthdate.getTime());
        }
        if (this.youngestGrade) {
            clone.youngestGrade = this.youngestGrade.clone();
        }
        if (this.oldestGrade) {
            clone.oldestGrade = this.oldestGrade.clone();
        }
        if (this.oldestGrade) {
            clone.oldestGrade = this.oldestGrade.clone();
        }
        if (this.weightClasses) {
            clone.weightClasses = this.weightClasses.map((wc: WeightClass) => wc.clone());
        }
        if (this.skillLevels) {
            clone.skillLevels = this.skillLevels.map((skill: SkillLevel) => skill.clone());
        }
        if (this.seedingCriteria) {
            clone.seedingCriteria = this.seedingCriteria.map((sc: SeedingCriteria) => sc.clone());
        }
        if (this.divisionRegistration) {
            clone.divisionRegistration = this.divisionRegistration.clone();
        }
        if (this.publishWrestlersDateTime) {
            clone.publishWrestlersDateTime = new Date(this.publishWrestlersDateTime.getTime());
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'division';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.name = this.name;
        json.data.attributes.sequence = this.sequence;
        json.data.attributes.weighInStartDateTimeUtc = null;
        json.data.attributes.weighInEndDateTimeUtc = null;
        json.data.attributes.isUsaWrestlingCardNumRequired = this.isUsaWrestlingCardNumRequired;
        json.data.attributes.isDual = this.isDual;
        json.data.attributes.isVarsity = isNil(this.isVarsity) ? false : this.isVarsity;
        json.data.attributes.isYoungestStrict = isNil(this.isYoungestStrict) ? false : this.isYoungestStrict;

        if (this.weighInStartDateTime && this.weighInEndDateTime) {
            json.data.attributes.weighInStartDateTimeUtc = this.weighInStartDateTime.toISOString();
            json.data.attributes.weighInEndDateTimeUtc = this.weighInEndDateTime.toISOString();
        }

        json.data.attributes.publishWrestlersDateTimeUtc = null;
        if (this.publishWrestlersDateTime) {
            json.data.attributes.publishWrestlersDateTimeUtc = this.publishWrestlersDateTime.toISOString();
        }

        json.data.attributes.youngestBirthdate = null;
        json.data.attributes.oldestBirthdate = null;
        if (this.youngestBirthdate) {
            json.data.attributes.youngestBirthdate = format(this.youngestBirthdate, DateFormat.YYYYMMDD);
        }
        if (this.oldestBirthdate) {
            json.data.attributes.oldestBirthdate = format(this.oldestBirthdate, DateFormat.YYYYMMDD);
        }

        json.data.relationships = {};

        json.data.relationships.youngestGrade = { data: { type: 'grade', id: null } };
        json.data.relationships.oldestGrade = { data: { type: 'grade', id: null } };

        if (this.youngestGrade) {
            json.data.relationships.youngestGrade.data.id = this.youngestGrade.id;
        }
        if (this.oldestGrade) {
            json.data.relationships.oldestGrade.data.id = this.oldestGrade.id;
        }

        json.data.attributes.measurementUnit = null;
        if (this.measurementUnit) {
            json.data.attributes.measurementUnit = this.measurementUnit;
        }

        const divisionProfile: any = {};
        divisionProfile.data = { type: 'divisionProfile', id: this.divisionProfileId };
        json.data.relationships.divisionProfile = divisionProfile;

        if (this.usawMemberships) {
            const usawMembership: any = { data: [] };

            for (const membership of this.usawMemberships) {
                usawMembership.data.push({ type: 'usawMembership', id: membership.id });
            }

            json.data.relationships.usawMemberships = usawMembership;
        }

        if (this.floLevelId) {
            json.data.relationships.floLevel = {
                data: { type: 'floLevel', id: this.floLevelId },
            };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: any, included: any[]): Division {
        const data: any = json.data || json;
        const attrs: any = data.attributes || {};
        const relationships: any = data.relationships || {};

        this.id = data.id;
        this.eventId = attrs.eventId;
        this.name = attrs.name;
        this.sequence = attrs.sequence;
        this.divisionGroupingType = attrs.divisionGroupingType;
        this.divisionProfileId = attrs.divisionProfileId;
        this.isUsaWrestlingCardNumRequired = attrs.isUsaWrestlingCardNumRequired;
        this.isDual = attrs.isDual;
        this.isVarsity = attrs.isVarsity;
        this.isYoungestStrict = attrs.isYoungestStrict;
        this.eventTimeZone = attrs.eventTimeZone;
        this.floLevelId = attrs.floLevelId;

        if (attrs.publishWrestlersDateTime) {
            this.publishWrestlersDateTime = convertToDateTime(attrs.publishWrestlersDateTime);
        }
        if (attrs.weighInStartDateTime) {
            this.weighInStartDateTime = convertToDateTime(attrs.weighInStartDateTime);
        }
        if (attrs.weighInEndDateTime) {
            this.weighInEndDateTime = convertToDateTime(attrs.weighInEndDateTime);
        }
        if (attrs.youngestBirthdate) {
            this.youngestBirthdate = convertToDateTime(attrs.youngestBirthdate);
        }
        if (attrs.oldestBirthdate) {
            this.oldestBirthdate = convertToDateTime(attrs.oldestBirthdate);
        }
        if (attrs.youngestGrade) {
            this.youngestGrade = (new Grade()).deserialize(attrs.youngestGrade);
        }
        if (attrs.oldestGrade) {
            this.oldestGrade = (new Grade()).deserialize(attrs.oldestGrade);
        }
        this.measurementUnit = attrs.measurementUnit;
        this.isUsaWrestlingCardNumRequired = attrs.isUsaWrestlingCardNumRequired;

        // Fill WeightClasses from relationships if it exists
        const weightClassLinks: any = relationships.weightClasses;
        if (weightClassLinks &&
            included &&
            weightClassLinks.data) {
            this.weightClasses = [];

            for (const weightClassLink of weightClassLinks.data) {
                const weightClass: any = included.filter((i: any) => i.id === weightClassLink.id)[0];
                if (weightClass) {
                    this.weightClasses.push((new WeightClass()).deserialize(weightClass));
                }
            }
        }

        // Fill SkillLevels from relationships if it exists
        const skillLevelLinks: any = relationships.skillLevels;
        if (skillLevelLinks &&
            included &&
            skillLevelLinks.data) {
            this.skillLevels = [];

            for (const skillLevelLink of skillLevelLinks.data) {
                const skillLevel: any = included.filter((i: any) => i.id === skillLevelLink.id)[0];
                if (skillLevel) {
                    this.skillLevels.push((new SkillLevel()).deserialize(skillLevel));
                }
            }
        }

        // Fill SeedingCriteria from relationships if it exists
        const seedingCriteriaLinks: any = relationships.seedingCriteria;
        if (seedingCriteriaLinks &&
            included &&
            seedingCriteriaLinks.data) {
            this.seedingCriteria = [];

            for (const seedingCriteriaLink of seedingCriteriaLinks.data) {
                const seedingCriteria: any = included.filter((i: any) => i.id === seedingCriteriaLink.id)[0];
                if (seedingCriteria) {
                    this.seedingCriteria.push((new SeedingCriteria()).deserialize(seedingCriteria));
                }
            }
        }

        // Fill DivisionRegistration from relationships if it exists
        const divisionRegistrationLink: any = relationships.divisionRegistration;
        if (divisionRegistrationLink &&
            included &&
            divisionRegistrationLink.data &&
            divisionRegistrationLink.data.id) {
            const divisionRegistrationId: string = divisionRegistrationLink.data.id;
            const divisionRegistration: any = included.filter(
                (i: any) => i.type === 'divisionRegistration' && i.id === divisionRegistrationId,
            )[0];

            if (divisionRegistration) {
                this.divisionRegistration = (new DivisionRegistration(this.eventId, this.id))
                    .deserialize(divisionRegistration, included);
            }
        }

        const usawMembershipList: any[] = get(data, 'relationships.usawMemberships.data') || [];
        usawMembershipList.forEach((x: any) => {
            this.usawMemberships = this.usawMemberships || [];
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                this.usawMemberships.push((new UsawMembership()).deserialize(item));
            }
        });

        return this;
    }
}
